import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Database,
  Download,
  FileAudio,
  Loader2,
  RefreshCw,
  Search,
  Volume2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { downloadRecordingViaPanel, isRecordingNotFoundMessage } from "@/utils/recordingDownload";

const PAGE_SIZES = [25, 50, 100];

const DATE_RANGE_BUTTONS = [
  { value: "all", label: "Tümü" },
  { value: "today", label: "Bugün" },
  { value: "yesterday", label: "Dün" },
  { value: "7days", label: "7 Gün" },
  { value: "custom", label: "Özel" },
];

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const resolveDateRange = (range, customStart, customEnd) => {
  const today = formatLocalDate(new Date());
  if (range === "today") return { start: today, end: today };
  if (range === "yesterday") {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = formatLocalDate(d);
    return { start: y, end: y };
  }
  if (range === "7days") {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return { start: formatLocalDate(d), end: today };
  }
  if (range === "custom") {
    return { start: customStart || "", end: customEnd || "" };
  }
  return { start: "", end: "" };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const shortenFilename = (name, max = 24) => {
  if (!name) return "-";
  const text = String(name);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3)}...`;
};

const shortenPath = (path) => {
  if (!path) return "-";
  const normalized = String(path).replace(/\\/g, "/");
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length <= 1) return normalized;
  return parts[parts.length - 1];
};

const mapRecordingRowToAudioRecord = (row, device, mode) => {
  const phone = row.phone_number || row.parsed_phone_number || "-";
  const dateValue = mode === "full"
    ? (row.file_date || row.synced_at)
    : (row.cagridate || row.uploaded_at || row.created_at);
  return {
    id: row.id,
    download_source: mode === "full" ? "device_recording_full" : "device_recording",
    device_id: device?.id ?? row.device_id ?? null,
    recording_id: row.id,
    arayan_numara: phone,
    aranan_numara: device?.gsm || "-",
    tarih: formatDateTime(dateValue),
    konusma_suresi: row.parsed_duration || 0,
    recording_path: row.recording_url,
    recording_url: row.recording_url,
  };
};

const DeviceRecordingsTable = ({
  API,
  device,
  deviceId,
  mode = "recordings",
  isDark,
  isActive,
  canSeeRecordings = true,
  onPlayRecording,
  onTotalChange,
  inputClass,
  labelClass,
}) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const endpoint = mode === "full" ? "device-recordings-full" : "device-recordings";
  const isFull = mode === "full";

  const activeDates = useMemo(
    () => resolveDateRange(dateRange, startDate, endDate),
    [dateRange, startDate, endDate]
  );

  const fetchRecords = useCallback(async () => {
    if (!deviceId || !isActive) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("device_id", String(deviceId));
      params.append("page", String(page));
      params.append("limit", String(limit));
      if (searchTerm) params.append("search", searchTerm);
      if (activeDates.start) params.append("start_date", activeDates.start);
      if (activeDates.end) params.append("end_date", activeDates.end);

      const res = await axios.get(`${API}/${endpoint}?${params}`, { timeout: 60000 });
      const rows = res.data.data || [];
      const nextTotal = res.data.total || 0;
      setRecords(rows);
      setTotal(nextTotal);
      setTotalPages(res.data.total_pages || 1);
      onTotalChange?.(nextTotal);
    } catch (error) {
      console.error(`${endpoint} error:`, error);
      setRecords([]);
      setTotal(0);
      setTotalPages(1);
      onTotalChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [API, activeDates.end, activeDates.start, deviceId, endpoint, isActive, limit, onTotalChange, page, searchTerm]);

  useEffect(() => {
    if (isActive && deviceId) fetchRecords();
  }, [fetchRecords, isActive, deviceId]);

  const handleDownload = async (row) => {
    const audioRecord = mapRecordingRowToAudioRecord(row, device, mode);
    const filename = isFull ? row.filename : row.original_filename;
    setDownloadingId(row.id);
    try {
      await downloadRecordingViaPanel({
        apiBase: API,
        source: audioRecord.download_source,
        deviceId: audioRecord.device_id,
        recordingId: audioRecord.recording_id,
        filename: filename || "",
      });
      toast.success("Ses dosyası indirildi");
    } catch (error) {
      const message = error.message || "Ses kaydı indirilemedi";
      if (isRecordingNotFoundMessage(message)) {
        toast.warning(message);
      } else {
        toast.error(message);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    setPage(1);
    setSearchTerm("");
    setSearchInput("");
    setDateRange("all");
    setStartDate("");
    setEndDate("");
  }, [deviceId, mode]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setPage(1);
    if (value === "custom" && !startDate && !endDate) {
      const today = formatLocalDate(new Date());
      setStartDate(today);
      setEndDate(today);
    }
  };

  const mutedClass = isDark ? "text-slate-500" : "text-gray-500";
  const cardClass = isDark
    ? "rounded-xl border border-slate-800 bg-slate-950/50 overflow-hidden"
    : "rounded-xl border border-gray-200 bg-gray-50/80 overflow-hidden";
  const title = isFull ? "Recordings Full" : "Recordings";
  const TitleIcon = isFull ? Database : FileAudio;
  const filterBarClass = isDark
    ? "rounded-lg border border-slate-800/80 bg-slate-950/40 p-2"
    : "rounded-lg border border-gray-200 bg-gray-50/70 p-2";

  return (
    <div className="space-y-3 max-w-4xl">
      <div className="flex flex-col gap-2 px-1 sm:px-2 pt-2">
        <div className="flex items-center gap-2 min-w-0">
          <TitleIcon className={`w-4 h-4 shrink-0 ${isDark ? (isFull ? "text-violet-400" : "text-cyan-400") : (isFull ? "text-violet-600" : "text-cyan-600")}`} />
          <p className={`text-sm font-semibold truncate ${isDark ? "text-slate-200" : "text-gray-800"}`}>
            {title}
          </p>
          <Badge
            variant="outline"
            className={`font-mono text-[10px] ${isDark ? "border-slate-600 text-slate-300" : "border-gray-300 text-gray-700"}`}
          >
            {total.toLocaleString("tr-TR")}
          </Badge>
        </div>

        <div className={`${filterBarClass} space-y-2`}>
          <div className="flex flex-wrap items-center gap-1.5">
            <div className={`inline-flex flex-wrap items-center rounded-lg p-0.5 ${isDark ? "bg-slate-800/90 ring-1 ring-slate-700/60" : "bg-gray-100 ring-1 ring-gray-200/80"}`}>
              {DATE_RANGE_BUTTONS.map((btn) => (
                <button
                  key={btn.value}
                  type="button"
                  onClick={() => handleDateRangeChange(btn.value)}
                  className={`h-7 px-2.5 text-[11px] font-medium rounded-md transition-all whitespace-nowrap ${
                    dateRange === btn.value
                      ? (isDark ? "bg-cyan-600 text-white shadow-sm" : "bg-white text-cyan-700 shadow-sm ring-1 ring-gray-200/80")
                      : (isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-700/50" : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/60")
                  }`}
                >
                  {btn.label}
                </button>
              ))}
            </div>

            {dateRange === "custom" && (
              <div className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 ${isDark ? "bg-slate-800/60 ring-1 ring-slate-700/50" : "bg-white ring-1 ring-gray-200"}`}>
                <div className="relative">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                    className={`w-[7rem] h-7 text-[11px] pl-6 rounded-md border-0 shadow-none ${isDark ? "bg-slate-900/80 text-white" : "bg-gray-50"} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                  />
                  <Calendar className={`w-3 h-3 absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                </div>
                <span className={`text-[10px] ${mutedClass}`}>—</span>
                <div className="relative">
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                    className={`w-[7rem] h-7 text-[11px] pl-6 rounded-md border-0 shadow-none ${isDark ? "bg-slate-900/80 text-white" : "bg-gray-50"} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
                  />
                  <Calendar className={`w-3 h-3 absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-cyan-400" : "text-cyan-600"}`} />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSearch} className="flex gap-1.5">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={isFull ? "Dosya veya yol ara..." : "Dosya adı veya numara ara..."}
              className={`h-7 flex-1 min-w-0 text-[11px] ${inputClass}`}
            />
            <Button type="submit" size="sm" className="h-7 px-2 shrink-0">
              <Search className="w-3.5 h-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={fetchRecords} disabled={loading} className="h-7 w-7 p-0 shrink-0">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </form>
        </div>
      </div>

      <div className={cardClass}>
        {loading ? (
          <div className={`flex items-center justify-center py-10 gap-2 ${mutedClass}`}>
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-xs">Yükleniyor…</span>
          </div>
        ) : records.length === 0 ? (
          <div className={`text-center py-10 text-xs ${mutedClass}`}>
            Kayıt bulunamadı.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="text-[11px] table-fixed w-full min-w-[640px] max-w-4xl">
              <TableHeader>
                <TableRow className={isDark ? "border-slate-800 hover:bg-transparent" : ""}>
                  <TableHead className={`w-10 px-2 ${isDark ? "text-slate-400" : ""}`}>ID</TableHead>
                  <TableHead className={`w-[22%] px-2 ${isDark ? "text-slate-400" : ""}`}>Dosya</TableHead>
                  <TableHead className={`w-[13%] px-2 ${isDark ? "text-slate-400" : ""}`}>
                    {isFull ? "Dosya Tarihi" : "Çağrı Tarihi"}
                  </TableHead>
                  <TableHead className={`w-[13%] px-2 ${isDark ? "text-slate-400" : ""}`}>
                    {isFull ? "Senkron" : "Yükleme Tarihi"}
                  </TableHead>
                  {!isFull && (
                    <TableHead className={`w-[12%] px-2 ${isDark ? "text-slate-400" : ""}`}>Numara</TableHead>
                  )}
                  <TableHead className={`w-[9%] px-2 text-right ${isDark ? "text-slate-400" : ""}`}>Boyut</TableHead>
                  <TableHead className={`w-[12%] px-2 ${isDark ? "text-slate-400" : ""}`}>Yol</TableHead>
                  {canSeeRecordings && (
                    <TableHead className={`w-16 px-1 text-center ${isDark ? "text-slate-400" : ""}`} />
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((row) => {
                  const filename = isFull ? row.filename : row.original_filename;
                  const pathLabel = isFull ? row.recording_path : row.original_filename;
                  const pathShort = shortenPath(pathLabel);
                  const filenameShort = shortenFilename(filename);
                  const canPlay = Boolean(row.recording_url);
                  return (
                    <TableRow key={row.id} className={isDark ? "border-slate-800/50" : ""}>
                      <TableCell className={`font-mono px-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{row.id}</TableCell>
                      <TableCell
                        className={`font-mono px-2 truncate cursor-default ${isDark ? "text-slate-200" : "text-gray-800"}`}
                        title={filename || "-"}
                      >
                        {filenameShort}
                      </TableCell>
                      <TableCell className={`px-2 whitespace-nowrap ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        {formatDateTime(isFull ? row.file_date : row.cagridate)}
                      </TableCell>
                      <TableCell className={`px-2 whitespace-nowrap ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        {formatDateTime(isFull ? row.synced_at : row.uploaded_at)}
                      </TableCell>
                      {!isFull && (
                        <TableCell className={`font-mono px-2 truncate ${isDark ? "text-amber-400" : "text-amber-600"}`} title={row.phone_number || "-"}>
                          {row.phone_number || "-"}
                        </TableCell>
                      )}
                      <TableCell className={`px-2 text-right whitespace-nowrap ${isDark ? "text-teal-400" : "text-teal-600"}`}>
                        {row.file_size_label || row.filesize_label || "-"}
                      </TableCell>
                      <TableCell
                        className={`font-mono px-2 truncate ${isDark ? "text-cyan-400/80" : "text-cyan-700"}`}
                        title={pathLabel || row.recording_url || "-"}
                      >
                        {pathShort}
                      </TableCell>
                      {canSeeRecordings && (
                        <TableCell className="px-1 text-center">
                          {canPlay ? (
                            <div className="inline-flex items-center gap-0.5">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onPlayRecording?.(row.recording_url, mapRecordingRowToAudioRecord(row, device, mode))}
                                className={`p-0.5 h-6 w-6 min-w-0 ${isDark ? "text-cyan-400 hover:text-cyan-300 hover:bg-slate-800" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}`}
                                title="Dinle"
                                aria-label="Dinle"
                              >
                                <Volume2 className="w-3.5 h-3.5 shrink-0" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(row)}
                                disabled={downloadingId === row.id}
                                className={`p-0.5 h-6 w-6 min-w-0 ${isDark ? "text-emerald-400 hover:text-emerald-300 hover:bg-slate-800" : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"}`}
                                title="İndir"
                                aria-label="İndir"
                              >
                                {downloadingId === row.id ? (
                                  <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" />
                                ) : (
                                  <Download className="w-3.5 h-3.5 shrink-0" />
                                )}
                              </Button>
                            </div>
                          ) : (
                            <span className={mutedClass}>-</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <div className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t ${isDark ? "border-slate-800" : "border-gray-200"}`}>
            <span className={`text-[11px] ${labelClass || mutedClass}`}>
              {total.toLocaleString("tr-TR")} kayıt · {page}/{totalPages}
            </span>
            <div className="flex items-center gap-1.5">
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                className={`h-7 text-[11px] rounded-md px-1.5 ${isDark ? "bg-slate-900 border border-slate-700 text-white" : "bg-white border border-gray-300"}`}
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} className={`h-7 w-7 p-0 ${isDark ? "border-slate-700" : ""}`}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)} className={`h-7 w-7 p-0 ${isDark ? "border-slate-700" : ""}`}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceRecordingsTable;
